// types
import { BreadCrumbs } from 'shared/types/breadCrumbs';

// helpers
import { getDomainConfig } from 'shared/domainConfig/helpers';
const ITEM_LIST_SCHEMA_LIMIT = 20;
/**
 * Safely serialize JSON for inline script context to prevent XSS attacks
 * Escapes < to \u003c to prevent </script> breakouts in SSR output
 * @param obj - Object to serialize
 * @returns Safe JSON string for innerHTML
 */
export const safeJSONStringify = (obj: unknown): string => {
  return JSON.stringify(obj).replace(/</g, '\\u003c');
};

/**
 * Generate CollectionPage schema for PLP/SLP
 * As per PRD requirements for category pages
 */
export const getCollectionPageSchema = ({
  name,
  description,
  url,
}: {
  name: string;
  description: string;
  url: string;
}) => {
  if (!name || !url) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    url,
    description: description || '',
  };
};

/**
 * Generate ItemList schema for PLP/SLP
 * As per PRD: Only position and url (no name, no pricing)
 */
export const getItemListSchema = (products: Listing.IProduct[], limit = 20) => {
  const { SHARE } = getDomainConfig();

  if (!products || products.length === 0) return null;

  // Limit to first N products as per PRD (recommended 10-20)
  const limitedProducts = products.slice(0, limit);

  const itemListElement = limitedProducts.map((product: Record<string, string>, index: number) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: [product.title, product.subTitle].filter(Boolean).join(' '),
    url: SHARE.CANONICAL_HOMEPAGE_URL + (product.actionUrl || product.action_url),
  }));

  return {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    itemListElement,
  };
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use getItemListSchema instead
 */
export const getPlpJSONLD = (products: Listing.IProduct[]) => {
  const schema = getItemListSchema(products, ITEM_LIST_SCHEMA_LIMIT);
  return schema ? JSON.stringify(schema) : JSON.stringify({});
};

export type FaqItem = { que: string; ans: string };

/**
 * Generate FAQPage schema for PLP/PDP
 * Returns null when there are no valid question/answer pairs
 */
export const getFaqPageSchema = (faq?: FaqItem[] | null) => {
  if (!faq?.length) return null;

  const mainEntity = faq
    .filter((item) => item?.que && item?.ans)
    .map((item) => ({
      '@type': 'Question',
      name: item.que,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.ans,
      },
    }));

  if (!mainEntity.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
};

/**
 * Generate BreadcrumbList schema
 * Returns complete schema (not nested in another object)
 */
export const getBreadcrumbListSchema = (breadCrumbs: BreadCrumbs) => {
  if (!breadCrumbs?.length) return null;

  const { SHARE } = getDomainConfig();

  const breadCrumbList = breadCrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb?.key,
    item: `${SHARE.CANONICAL_HOMEPAGE_URL}${crumb?.value}`,
  }));

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbList,
  };
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use getBreadcrumbListSchema instead
 */
export const getBreadCrumbsData = (breadCrumbs: BreadCrumbs) => {
  if (!breadCrumbs.length) return;

  const data = { breadCrumbs: {} };
  const { SHARE } = getDomainConfig();

  const breadCrumbList = breadCrumbs.map((crumb, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: crumb?.key,
      item: `${SHARE.CANONICAL_HOMEPAGE_URL}${crumb?.value}`,
    };
  });

  data.breadCrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbList,
  };

  return data;
};
